import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { isDockEmbedded } from '../utils/dockEmbed';

// Inside the /acp ToolsDock a tool runs framed (utils/dockEmbed.js). That frame is for TOOL pages
// only: anything else a link or ⌘K could reach in there (QA Manager, Home, …) is not rendered —
// the frame shows a notice with a link that opens the page as its own Clara tab instead.
const DOCK_FRAME_PATHS = [/^\/tools(\/|$)/];
export const allowedInDockFrame = (pathname) => DOCK_FRAME_PATHS.some((re) => re.test(String(pathname || '')));

// (`inline-block`, not `inline-flex`: index.css forces `color: inherit !important` on a[class*="inline-flex"].)
const DockFrameBlocked = ({ href }) => (
  <div className="min-h-screen flex items-center justify-center bg-white dark:bg-[#0A0A0B] px-6 text-center">
    <div className="max-w-[300px]">
      <p className="text-[14px] font-semibold text-gray-900 dark:text-white">This page does not open inside the ACP dock.</p>
      <p className="mt-1.5 text-[12.5px] leading-relaxed text-gray-500 dark:text-white/50">The dock is for tools only.</p>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-4 inline-block rounded-lg bg-gray-900 dark:bg-white px-3.5 py-2 text-[12.5px] font-semibold text-white dark:text-[#111111] hover:opacity-90 transition-opacity"
      >
        Open in Clara
      </a>
    </div>
  </div>
);

const PrivateRoute = ({ children, adminOnly = false, requiredRoles, requiredCap, allow }) => {
  const { isAuthenticated, isAdmin, user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-foreground">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/dashboard" />;
  }

  if (requiredRoles && !requiredRoles.includes(user?.role)) {
    // Per-user capability grant can stand in for a missing role
    if (!(requiredCap && (user?.qaCapabilities || []).includes(requiredCap))) {
      return <Navigate to="/dashboard" />;
    }
  }

  // a page with its own rule (e.g. config/simulationsAccess.js): `allow(user)` decides
  if (allow && !allow(user)) {
    return <Navigate to="/dashboard" />;
  }

  if (isDockEmbedded() && !allowedInDockFrame(location.pathname)) {
    return <DockFrameBlocked href={`${location.pathname}${location.search || ''}`} />;
  }

  return children;
};

export default PrivateRoute;
